import { useCallback, useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { useAppDispatch, useAppStore } from '../redux';
import { markBoundsFitted } from '../redux/reducer';
import useMap from './useMap';

/**
 * Reads and records `state.map.fittedBoundsKeys` for a single call site. Prefer useFitBoundsOnce;
 * use this when the fit can fail and has to be retried, so only a successful fit is recorded.
 *
 * The store is read directly rather than through a selector because nothing renders off this state.
 */
export const useFitBoundsMemory = (id: string) => {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const hasFitted = useCallback((key: string) => store.getState().map.fittedBoundsKeys[id] === key, [store, id]);
  const markFitted = useCallback((key: string) => dispatch(markBoundsFitted({ id, key })), [dispatch, id]);

  return { hasFitted, markFitted };
};

/**
 * Runs `fit` once per distinct `key`, remembering what it framed in `state.map.fittedBoundsKeys` so
 * a remount doesn't re-frame. `id` scopes the memory to this call site; pass `key: undefined` when
 * there is nothing to frame yet.
 */
const useFitBoundsOnce = (id: string, key: string | undefined, fit: (map: MapRef) => void) => {
  const map = useMap();
  const { hasFitted, markFitted } = useFitBoundsMemory(id);

  // Callers pass an inline closure that changes every render, so hold it in a ref and let `key` decide when to re-frame
  const fitRef = useRef(fit);
  useEffect(() => {
    fitRef.current = fit;
  });

  useEffect(() => {
    if (!map || key === undefined || hasFitted(key)) {
      return;
    }

    markFitted(key);
    fitRef.current(map);
  }, [map, key, hasFitted, markFitted]);
};

export default useFitBoundsOnce;
